import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircle, Info } from 'lucide-react';
import Popover from './Popover';

interface HelpTipProps {
  /** The part being explained: the panel title and the button's accessible name. */
  title: string;
  /**
   * The explanation, plain text from i18n. Each line is a paragraph; a line that
   * starts with "• " is a bullet (consecutive bullets form one list).
   */
  body: string;
  className?: string;
  /** "help" (the round "?", default) or "info" (the round "i", for a note on what the numbers do not include yet). */
  icon?: 'help' | 'info';
  /** P-37 (D-194) — also opens while the pointer rests on the "?" (a click still opens / closes it). */
  openOnHover?: boolean;
}

/**
 * "?" next to a section title (owner, 2026-10-11): a quiet round icon that opens
 * a short explanation of what the section shows, in an anchored `ui/Popover`
 * (role=dialog, Escape / outside press close it and focus returns to the "?").
 * Muted until hovered, so it never competes with the section's own actions.
 *
 * The panel is named by the title and described by the body
 * (`aria-describedby`), so a screen reader reads both when focus lands on it.
 * Under a coarse pointer the icon keeps its 24 px size; only the press area
 * grows to 44 px (`.help-tip::after` in index.css), so titles are not pushed.
 */
export default function HelpTip({ title, body, className = '', icon = 'help', openOnHover = false }: HelpTipProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const titleId = useId();
  const bodyId = useId();

  const blocks: Array<{ kind: 'p'; text: string } | { kind: 'ul'; items: string[] }> = [];
  for (const raw of body.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    if (line.startsWith('• ')) {
      const last = blocks[blocks.length - 1];
      if (last?.kind === 'ul') last.items.push(line.slice(2));
      else blocks.push({ kind: 'ul', items: [line.slice(2)] });
    } else {
      blocks.push({ kind: 'p', text: line });
    }
  }

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={openOnHover ? () => setOpen(true) : undefined}
        onMouseLeave={openOnHover ? () => setOpen(false) : undefined}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-haspopup="dialog"
        aria-label={t('help.buttonLabel', { topic: title })}
        className={`help-tip inline-flex shrink-0 items-center justify-center rounded-full p-1 text-text-3 transition-colors duration-micro
          hover:bg-surface-subtle hover:text-text-2 aria-expanded:bg-surface-subtle aria-expanded:text-accent-text
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus ${className}`}
      >
        {icon === 'info' ? <Info size={16} strokeWidth={1.75} aria-hidden /> : <HelpCircle size={16} strokeWidth={1.75} aria-hidden />}
      </button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        align="start"
        id={panelId}
        role="dialog"
        ariaLabelledBy={titleId}
        ariaDescribedBy={bodyId}
        focusFirst={!openOnHover}
        className="w-80 max-w-[calc(100vw-2rem)] p-4"
      >
        <p id={titleId} className="text-sm font-semibold text-text">{title}</p>
        <div id={bodyId} className="mt-2 space-y-2 text-sm leading-relaxed text-text-2">
          {blocks.map((b, i) => (b.kind === 'p'
            ? <p key={i}>{b.text}</p>
            : (
              <ul key={i} className="list-disc space-y-1 ps-5 marker:text-text-3">
                {b.items.map((item, j) => <li key={j}>{item}</li>)}
              </ul>
            )))}
        </div>
      </Popover>
    </>
  );
}
